import i18next, { type i18n as I18nInstance, type Resource } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

type CreateI18nBase = {
  resources: Resource;
  defaultLng: string;
  ns?: string | string[];
  defaultNs?: string;
  fallbackLng?: string;
  debug?: boolean;
};

export type CreateI18nOptions = CreateI18nBase;

export type CreateI18nInstanceOptions = CreateI18nBase & {
  /**
   * The language to pin this instance to. Required for SSR — the caller
   * resolves it from session / Accept-Language / default before calling.
   */
  lng: string;
};

/**
 * Initialize the process-wide i18next singleton for an SPA.
 *
 * Wires `i18next-browser-languagedetector` (cascades through querystring →
 * cookie → localStorage → navigator → htmlTag) and falls back to
 * `defaultLng`. The detector reads localStorage under the default key
 * `i18nextLng`, so language persistence works without manual storage
 * handling here.
 *
 * SSR apps must NOT use this — concurrent requests with different
 * languages would race on the shared singleton's `lng`. Use
 * `createI18nInstance` instead.
 */
export function createI18n(options: CreateI18nOptions): I18nInstance {
  const {
    resources,
    defaultLng,
    ns = 'translations',
    defaultNs = 'translations',
    fallbackLng = defaultLng,
    debug = false,
  } = options;

  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  i18next
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      fallbackLng,
      debug,
      ns,
      defaultNS: defaultNs,
      interpolation: { escapeValue: false },
    });

  return i18next;
}

/**
 * Build an isolated i18next instance, pinned to `lng`.
 *
 * Use this for SSR: each request creates its own instance, so concurrent
 * requests can render in different languages without sharing mutable
 * state. Pair with `<I18nextProvider i18n={instance}>` so consuming
 * components' `useTranslation()` reads from the per-request instance.
 *
 * For inline `resources`, init resolves synchronously — the returned
 * instance is usable immediately for `t()` calls and SSR rendering.
 */
export function createI18nInstance(options: CreateI18nInstanceOptions): I18nInstance {
  const {
    resources,
    defaultLng,
    lng,
    ns = 'translations',
    defaultNs = 'translations',
    fallbackLng = defaultLng,
    debug = false,
  } = options;

  const instance = i18next.createInstance();
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  instance.use(initReactI18next).init({
    resources,
    lng,
    fallbackLng,
    debug,
    ns,
    defaultNS: defaultNs,
    interpolation: { escapeValue: false },
  });

  return instance;
}
